/**
 * Shared validation rules for funding round dates.
 *
 * The same rules apply wherever rounds are entered — the live edit page
 * (ManageFundingRounds in `live` mode), the create page's draft round
 * editor (ManageFundingRounds in `draft` mode), and the autofill dialog
 * which surfaces LLM-proposed rounds.
 *
 * Controller integration:
 *
 *   <Controller
 *     name="application_open_date"
 *     rules={{
 *       validate: (value) =>
 *         validateOpenDate(value, getValues('application_close_date')),
 *     }}
 *     ...
 *   />
 */

export type DateValue = Date | null | undefined

export const isInvalidDate = (value: DateValue): boolean =>
  Boolean(value) && isNaN((value as Date).getTime())

/**
 * Validate an opening date against an optional closing date.
 *
 * Returns `true` when valid; returns an error message string otherwise so
 * the result drops straight into a react-hook-form `validate` rule.
 */
export const validateOpenDate = (
  value: DateValue,
  closeDate: DateValue,
): true | string => {
  if (isInvalidDate(value)) return 'Invalid date'
  if (closeDate && !isNaN(closeDate.getTime())) {
    if (!value) return 'Opening date is required when a closing date is set'
    if ((value as Date) > closeDate)
      return 'Opening date must be before closing date'
  }
  return true
}

/**
 * Validate a closing date against an optional opening date.
 */
export const validateCloseDate = (
  value: DateValue,
  openDate: DateValue,
): true | string => {
  if (isInvalidDate(value)) return 'Invalid date'
  if (openDate && !isNaN(openDate.getTime())) {
    if (!value) return 'Closing date is required when an opening date is set'
    if ((value as Date) < openDate)
      return 'Closing date must be after opening date'
  }
  return true
}

export interface RoundDates {
  application_open_date: DateValue
  application_close_date: DateValue
  decision_date: DateValue
}

export interface RoundValidationResult {
  isValid: boolean
  errors: {
    application_open_date?: string
    application_close_date?: string
  }
}

/**
 * Validate a whole round at once. Useful for the autofill dialog which
 * shows N proposed rounds and needs to flag invalid ones before Apply.
 */
export const validateRound = (round: RoundDates): RoundValidationResult => {
  const openResult = validateOpenDate(
    round.application_open_date,
    round.application_close_date,
  )
  const closeResult = validateCloseDate(
    round.application_close_date,
    round.application_open_date,
  )
  return {
    isValid: openResult === true && closeResult === true,
    errors: {
      application_open_date:
        typeof openResult === 'string' ? openResult : undefined,
      application_close_date:
        typeof closeResult === 'string' ? closeResult : undefined,
    },
  }
}
